import { MouseEvent } from "react";
import { useBoolean, useDisclosure } from "@chakra-ui/react";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { useCustomActivityMutation } from "src/api/client/custom-activity.api";
import { EnumActivityType, useGenericActivityMutation } from "src/api/client/generic-activity.api";
import { Button, ButtonProps } from "src/components/base/button";
import { IInvestNowModalProps } from "src/components/modals/invest-now-modal/invest-now-modal";
import { GTM_ACTION_INVEST_CLICK, GTM_INVEST_BUTTON } from "src/consts/gtm";

interface IInvestButtonProps extends ButtonProps {
    wikifolioId: string;
    isin: string;
    wkn?: string | null;
    wikifolioSecurityId?: string;
}

const InvestNowModal = dynamic<IInvestNowModalProps>(
    () => import("src/components/modals/invest-now-modal/invest-now-modal").then(component => component.InvestNowModal),
    {
        ssr: false,
    }
);

export const InvestButton = ({ isin, wikifolioId, wikifolioSecurityId, wkn, onClick, children, ...buttonProps }: IInvestButtonProps) => {
    const { t } = useTranslation("common");
    const modalDisclosure = useDisclosure();
    const [isLoading, setLoading] = useBoolean();
    const createCustomActivity = useCustomActivityMutation();
    const genericActivity = useGenericActivityMutation();

    const handleOnClick = (event: MouseEvent<HTMLButtonElement>) => {
        setLoading.on();
        modalDisclosure.onOpen();
        onClick?.(event);
    };

    return (
        <>
            <Button
                onClick={handleOnClick}
                {...buttonProps}
                className={GTM_INVEST_BUTTON}
                isLoading={isLoading}
                data-isin={isin}
                data-gtm-action={GTM_ACTION_INVEST_CLICK}
            >
                {children || t("invest")}
            </Button>

            {modalDisclosure.isOpen && (
                <InvestNowModal
                    isin={isin}
                    wikifolioId={wikifolioId}
                    wikifolioSecurityId={wikifolioSecurityId}
                    wkn={wkn}
                    onRequestSuccess={() => {
                        createCustomActivity({ activity: "InvestClicked", value: isin });
                        genericActivity({
                            activityTypeId: EnumActivityType.InvestButtonPressed,
                            parameter: `wikifolioId=${wikifolioId}`,
                        });
                    }}
                    onRequestSettled={setLoading.off}
                    {...modalDisclosure}
                />
            )}
        </>
    );
};
